<template>
	<div class="order-status-page">
		<div
			class="desktop-center"
			v-if="this.orderStatus.sub_orders != undefined"
		>
			<div class="order-title">
				<div>
					<i :class="deliveryModeIconClass"></i>
					Tracking
				</div>
				<div>#{{ orderId }}</div>
			</div>
			<Banner>
				<div class="banner-eta-wrapper" slot="bannerInfo">
					<div class="banner-eta-title">Estimated Arrival Time</div>
					<div class="banner-eta">{{ etaText }}</div>
				</div>
			</Banner>
			<div class="order-status-title">
				{{ currentMilestone.statusTitle }}
			</div>
			<div class="order-progress">
				<svg>
					<line
						x1="10"
						y1="10"
						x2="100%"
						y2="10"
						stroke-width="3"
						stroke="#265D60"
						stroke-dasharray="3 3"
					/>
					<line
						x1="10"
						y1="10"
						:x2="lastIndicatorX"
						y2="10"
						stroke-width="3"
						stroke="#265D60"
						stroke-dasharray="3 3"
					/>
				</svg>
				<div class="progress-milestones">
					<div
						v-for="(milestone, i) in milestones"
						class="progress-milestone"
						:key="i"
						:class="{
							complete: milestone.complete,
							'current-milestone': milestone.current
						}"
						:style="{ left: milestone.leftStyle }"
					>
						<div class="progress-indicator"></div>
						<div class="progress-description">
							<div>{{ milestone.text }}</div>
							<div>{{ milestone.time }}</div>
						</div>
					</div>
				</div>
			</div>
			<div class="order-progress-text">
				<img
					v-if="showLockerQr"
					:src="lockerQr.src"
					alt="Locker QR Code"
				/>
				<i v-else :class="currentMilestone.progressIcon"></i>
				{{ currentMilestone.progressText }}
			</div>
		</div>
		<div class="sk-widget-alt order-info-widget desktop-center">
			<div>
				<div class="customer-info">
					<span class="customer-name text-truncate">
						{{ customerName }}
					</span>
					{{ customerPhone }}
				</div>
				<div class="order-info-container">
					<div class="order-info" v-html="deliveryText"></div>
					<div class="seperator"></div>
					<div class="order-info">
						<b>
							{{ productQuantity }}
							{{ productQuantity | pluralize("Item") }}
						</b>
					</div>
				</div>
			</div>
			<button class="button button-text" @click="openReceipt">
				<i class="sk-icon-receipt-solid"></i>
				View Receipt
			</button>
			<ReceiptModal ref="receiptModal" />
		</div>
	</div>
</template>

<style scoped>
.order-status-page {
	height: calc(100% - 200px);
	overflow-y: auto;
	padding-bottom: 20px;
}

@media only screen and (max-width: 670px) {
	.order-status-page {
		height: calc(100% - 50px);
		overflow-x: hidden;
		overflow-y: auto;
		padding-bottom: 170px;
	}
}

.desktop-center {
	max-width: 700px !important;
}

.order-status-page >>> .banner-info {
	height: 50px;
}

.order-status-page .banner-wrapper {
	margin-bottom: 0;
	margin-top: 0;
}

.order-title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 50px;
	padding: 0 18px;
}

.order-title > div,
.order-title i {
	color: var(--gR-base-80);
	font-size: 12px;
	font-weight: var(--sk-font-bold);
}

.order-title i {
	margin-right: 10px;
}

.banner-eta-wrapper {
	display: flex;
	justify-content: space-between;
	width: 100%;
}

.banner-eta-title,
.banner-eta {
	color: var(--gR-white);
	font-weight: var(--sk-font-bold);
}

.banner-eta-title {
	font-size: 14px;
}

.banner-eta {
	font-size: 12px;
}

.order-status-title {
	height: 51px;
	border: 1px solid var(--gR-white2);
	border-top: 0;
	border-bottom-left-radius: 5px;
	border-bottom-right-radius: 5px;
	margin: 0 18px;
	padding-left: 18px;
	font-size: 12px;
	color: var(--sk-grey3);
	display: flex;
	align-items: center;
}

.order-progress {
	width: 334px;
	height: 67px;
	margin: 32px auto 0 auto;
}

.order-progress svg {
	width: 100%;
	height: 25px;
}

.progress-milestones {
	display: flex;
	margin-top: -25px;
	position: relative;
}

.progress-milestone {
	position: absolute;
}

.progress-description {
	position: absolute;
	top: 32px;
	left: 50%;
	transform: translate(-50%, 0);
	white-space: nowrap;
}

.progress-description > div:first-child {
	color: var(--sk-grey3);
	font-weight: var(--sk-font-bold);
	font-size: 12px;
}

.progress-description > div:last-child {
	color: var(--sk-grey3);
	font-size: 10px;
}

.progress-milestone.complete .progress-description > div:first-child {
	color: var(--gR-base-80);
}

.progress-indicator {
	width: 20px;
	height: 20px;
	background: var(--gR-white);
	border-radius: 50%;
	border: 1px solid var(--sk-grey3);
	position: relative;
	margin-bottom: 15px;
}

.progress-milestone.complete .progress-indicator {
	background: var(--gR-teal-70);
	border-color: var(--gR-teal-70);
}

.progress-milestone.current-milestone .progress-indicator {
	background: var(--gR-teal-70);
	border: 1px solid var(--gR-teal-70);
}

.progress-milestone.current-milestone .progress-indicator:before {
	content: " ";
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	border: 2px solid var(--gR-white);
	width: 100%;
	height: 100%;
	z-index: 100;
	border-radius: 50%;
}

.order-progress-text {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-top: 75px;
	font-weight: var(--sk-font-bold);
}

.order-progress-text,
.order-progress-text i {
	color: var(--gR-base-80);
}

.order-progress-text i {
	font-size: 90px;
	min-height: 90px;
}

.order-progress-text i,
.order-progress-text img {
	margin-bottom: 20px;
}

.order-progress-text img {
	width: 125px;
	height: 125px;
}

.order-info-widget {
	display: flex;
	align-items: center;
	position: absolute;
	bottom: 0;
	left: 15px;
	right: 15px;
	bottom: 5px;
	width: auto;
}

.order-info-widget .button-text {
	display: flex;
	flex-direction: column;
	margin: 0 15px 0 auto;
	flex: 0;
	color: var(--gR-base-80);
	font-weight: var(--sk-font-bold);
	white-space: nowrap;
	font-size: 10px;
}

.order-info-widget .button-text i {
	color: var(--gR-base-80);
	font-size: 15px;
	margin-bottom: 10px;
}

.order-info-container {
	display: flex;
	align-items: center;
	margin-top: 20px;
}

.customer-info {
	display: flex;
	align-items: baseline;
}

.customer-info,
.order-info,
.order-info b {
	font-size: 10px;
	color: var(--sk-grey3);
}

.customer-info,
.order-info b {
	font-weight: var(--sk-font-bold);
}

.order-info >>> span {
	margin: 0 7px;
}

.order-info,
.seperator {
	display: inline-block;
}

.seperator {
	margin: 0 30px;
	height: 22px;
	border-left: 1px solid var(--gR-white2);
}

.customer-name {
	font-size: 14px;
	color: var(--gR-base-80);
	margin-right: 18px;
	max-width: 60%;
}

@media (min-width: 500px) {
	.order-info-widget {
		bottom: 15px;
	}

	.customer-name {
		max-width: 100%;
	}
}
</style>

<script>
import QRCode from "davidshimjs-qrcodejs"
import moment from "moment"
import "moment-timezone"
import { DeliveryMode } from "@reef-os/utils/enums/deliveryMode"
import { CourierId } from "@reef-os/utils/enums/courierId"
import loaderMixin from "@/mixins/loader-mixin"
import orderSuccessMixin from "@/mixins/order-success-mixin"
import phoneFilterMixin from "@/mixins/phone-filter-mixin"
import pluralizeFilterMixin from "@/mixins/pluralize-filter-mixin"
import locationNameFormatterMixin from "@/mixins/location-name-formatter-mixin"
import Banner from "@/components/Banner.vue"
import ReceiptModal from "@/components/modals/ReceiptModal.vue"

export default {
	name: "AirportOrderStatus",
	mixins: [
		orderSuccessMixin,
		loaderMixin,
		locationNameFormatterMixin,
		phoneFilterMixin,
		pluralizeFilterMixin
	],
	components: {
		Banner,
		ReceiptModal
	},
	filters: {
		formatTime(date, timezone) {
			return date.tz(timezone).format("h:mm A")
		}
	},
	data() {
		return {
			timer: "",
			lockerQr: {
				src: null,
				el: null,
				qrcode: null
			}
		}
	},
	created() {
		const deliveryId = this.firstSuborder
			? this.firstSuborder.delivery_job_id
			: null
		if (deliveryId) {
			this.$store.dispatch("getDelivery", deliveryId)
		}
		this.timer = setInterval(this.getOrderStatus, 60000)
		this.$store.dispatch("setInPurchaseState", false)
	},
	computed: {
		business() {
			return this.$store.state.business
		},
		deliveredTimeSlot() {
			return this.$store.state.deliveredDate
				? this.$options.filters.formatTime(
						moment.utc(this.$store.state.deliveredDate),
						this.business.timezone
				  )
				: ""
		},
		deliveryStatusId() {
			return this.$store.state.deliveryStatusId
		},
		lockerDeliveryStatus() {
			let lockerStatus
			if (
				this.$store.state.delivery &&
				this.$store.state.delivery.locker_delivery
			) {
				lockerStatus = this.$store.state.delivery.locker_delivery.status
			}
			return lockerStatus
		},
		lockerQrCode() {
			return (
				this.$store.state.delivery &&
				this.$store.state.delivery.locker_delivery &&
				this.$store.state.delivery.status >= 3 &&
				this.$store.state.delivery.locker_delivery.pickup_code
			)
		},
		deliveryModeIconClass() {
			switch (this.deliveryMode) {
				case DeliveryMode.DIRECT_TO_LOCATION:
					return "sk-icon-person-carry-box-solid"
				case DeliveryMode.LOCKER:
					return "sk-icon-door-open-solid"
			}
			return ""
		},
		lastIndicatorX() {
			let offset = 0
			this.milestones.forEach((milestone) => {
				if (milestone.complete) {
					offset = milestone.leftXOffset
				}
			})
			return `${offset}%`
		},
		milestones() {
			if (this.deliveryMode == DeliveryMode.LOCKER) {
				return [
					{
						statusTitle:
							"Look out for texts with more updates on your order!",
						text: "Placed",
						time: this.createdTime,
						leftStyle: 0,
						leftXOffset: 0,
						complete: true,
						current: !this.acceptedTime,
						progressIcon: "sk-icon-utensils-solid",
						progressText: "Order Placed..."
					},
					{
						statusTitle:
							"Look out for a text that your order is ready!",
						text: "Preparing",
						time: this.acceptedTime,
						leftStyle: "calc(50% - 10px)",
						leftXOffset: 50,
						complete: this.acceptedTime != null,
						current:
							!this.deliveredTime &&
							this.acceptedTime != null &&
							this.lockerDeliveryStatus == 0,
						progressIcon: "sk-icon-user-chef-regular",
						progressText: "Preparing Order..."
					},
					{
						statusTitle: "Enjoy your order!",
						text: "Ready",
						time: this.deliveredTime,
						leftStyle: "calc(100% - 20px)",
						leftXOffset: 100,
						complete: this.lockerDeliveryStatus > 0,
						current: this.lockerDeliveryStatus > 0,
						progressText: "Ready for Pickup..."
					}
				]
			} else {
				return [
					{
						statusTitle:
							"Look out for texts with more updates on your order!",
						text: "Placed",
						time: this.createdTime,
						leftStyle: 0,
						leftXOffset: 0,
						complete: true,
						current: !this.acceptedTime,
						progressIcon: "sk-icon-utensils-solid",
						progressText: "Order Placed..."
					},
					{
						statusTitle:
							"Look out for a text that your order is ready!",
						text: "Preparing",
						time: this.acceptedTime,
						leftStyle: "30%",
						leftXOffset: 35,
						complete: this.acceptedTime != null,
						current:
							this.acceptedTime != null && !this.pickedUpTime,
						progressIcon: "sk-icon-user-chef-regular",
						progressText: "Preparing Order..."
					},
					{
						statusTitle:
							"Look out for a text that your order is ready!",
						text: "Picked up",
						time: this.pickedUpTime,
						leftStyle: "60%",
						leftXOffset: 65,
						complete: this.deliveryStatusId >= 3, 
						current: this.deliveryStatusId === 3,
						progressIcon: "sk-icon-person-carry-box-solid",
						progressText: "Order Picked Up..."
					},
					{
						statusTitle: "Enjoy your order!",
						text: "Delivered",
						time: this.deliveredTimeSlot,
						leftStyle: "calc(100% - 20px)",
						leftXOffset: 100,
						complete: this.deliveryStatusId == 4 || this.orderStatus.sub_orders[0].status == 2 && this.deliveryStatusId == 3,
						current: this.deliveryStatusId == 4,
						progressIcon: "sk-icon-party-horn-solid",
						progressText: "Order Delivered!"
					}
				]
			}
		},
		currentMilestone() {
			const milestone = this.milestones.find(
				(milestone) => milestone.current
			)
			if (!milestone) {
				return {
					text: "",
					time: "",
					progressIcon: "",
					progressText: "",
					statusTitle: ""
				}
			}
			return milestone
		},
		showLockerQr() {
			return this.deliveryMode == DeliveryMode.LOCKER && this.lockerQrCode
		},
		customerName() {
			return this.orderStatus ? this.orderStatus.user_name : "N/A"
		},
		customerPhone() {
			return this.orderStatus
				? this.$options.filters.formatInternationalPhone(
						this.orderStatus.user_phone
				  )
				: "N/A"
		},
		hasSuborders() {
			return (
				this.orderStatus.sub_orders != undefined &&
				this.orderStatus.sub_orders.length > 0
			)
		},
		firstSuborder() {
			return this.hasSuborders ? this.orderStatus.sub_orders[0] : {}
		},
		etaText() {
			const deliveryStatus = this.$store.state.deliveryStatus
			const orderCreatedTime = moment(this.$store.state.orderStatus.created_time);
			const deliveryDropoffEta = moment.utc(deliveryStatus.dropoff_eta)
			const acceptableETA = deliveryDropoffEta.diff(orderCreatedTime, 'minutes')
			const bottleneckStandardETA = 30

			if (deliveryStatus == null || !deliveryStatus.dropoff_eta) {
				return "TBD"
			} else if (acceptableETA <= bottleneckStandardETA) { 
				const startEta = moment.utc(deliveryStatus.dropoff_eta).tz(this.business.timezone).add(-5, "m").format("h:mm A")
				const endEta = moment.utc(deliveryStatus.dropoff_eta).tz(this.business.timezone).add(5, "m").format("h:mm A")
				return `${startEta} - ${endEta}`
			} else {
				const nowStartEta = moment().utc().tz(this.business.timezone).add(25, "m").format("h:mm A")
				const nowEndEta = moment().utc().tz(this.business.timezone).add(35, "m").format("h:mm A")
				return `${nowStartEta} - ${nowEndEta}`
			}
		},
		createdTime() {
			if (!this.hasSuborders) {
				return null
			}
			return this.$options.filters.formatTime(
				moment.utc(this.orderStatus.created_time),
				this.business.timezone
			)
		},
		acknowledgeTime() {
			if (!this.hasSuborders || !this.firstSuborder.acknowledged_time) {
				return null
			}
			return this.$options.filters.formatTime(
				moment.utc(this.firstSuborder.acknowledged_time),
				this.business.timezone
			)
		},
		acceptedTime() {
			if (!this.hasSuborders || !this.firstSuborder.accept_time) {
				return null
			}
			return this.$options.filters.formatTime(
				moment.utc(this.firstSuborder.accept_time),
				this.business.timezone
			)
		},
		pickedUpTime() {
			if (!this.hasSuborders || !this.firstSuborder.pickup_time) {
				return null
			}
			return this.$options.filters.formatTime(
				moment.utc(this.firstSuborder.pickup_time),
				this.business.timezone
			)
		},
		deliveredTime() {
			if (!this.hasSuborders) {
				return null
			}

			let time = null

			const isCourierWalking =
				this.hasSuborders &&
				this.firstSuborder.delivery_id == CourierId.WALKING
			const isCourierTookan =
				this.hasSuborders &&
				this.firstSuborder.delivery_id == CourierId.TOOKAN

			if (isCourierWalking || isCourierTookan) {
				time = this.firstSuborder.completed_time
					? this.firstSuborder.completed_time
					: null
			} else {
				time = this.firstSuborder.delivered_time
					? this.firstSuborder.delivered_time
					: null
			}

			if (!time) {
				return null
			}
			return this.$options.filters.formatTime(
				moment.utc(time),
				this.business.timezone
			)
		},
		deliveryText() {
			switch (this.deliveryMode) {
				case DeliveryMode.DIRECT_TO_LOCATION: {
					let locationId = this.orderStatus.dropoff_location_id
					let gateNumber
					let dropoffLocations =
						this.$store.state.business.locations[0]
							.dropoff_locations

					let gateNumberText = dropoffLocations.filter(function (el) {
						if (el.id == locationId) {
							gateNumberText = el.text
						}
						gateNumber = gateNumberText
						return gateNumber
					})

					return `<b>Gate Delivery</b><span>to</span><b>Gate ${gateNumber}</b>`
				}
				case DeliveryMode.LOCKER: {
					return "<b>Pickup</b><span>at</span><b>Lockers</b>"
				}
			}
			return "N/A"
		},

		productQuantity() {
			if (this.firstSuborder) {
				return this.firstSuborder.products.reduce(
					(total, product) => total + product.quantity,
					0
				)
			}
			return 0
		}
	},
	methods: {
		openReceipt() {
			if (this.orderStatus && this.$store.state.brands) {
				this.$refs.receiptModal.open()
				this.$store.dispatch("getReceipt", { orderId: this.orderId })
			}
		},
		getOrderStatus() {
			this.$store.dispatch("getOrderStatus", { orderId: this.orderId })
		},
		configureLockerQr(text) {
			this.lockerQr.el = document.createElement("div")
			this.lockerQr.qrcode = new QRCode(this.lockerQr.el, {
				text: text,
				width: 125,
				height: 125,
				correctLevel: QRCode.CorrectLevel.H
			})
		},
		syncLockerQrSrc() {
			if (
				this.lockerQr.el.firstElementChild &&
				this.lockerQr.el.firstElementChild.toDataURL
			) {
				this.lockerQr.src =
					this.lockerQr.el.firstElementChild.toDataURL()
			}
		}
	},
	beforeDestroy() {
		clearInterval(this.timer)
	},
	watch: {
		lockerQrCode: {
			immediate: true,
			handler(newVal) {
				if (newVal) {
					if (!this.lockerQr.qrCode) {
						this.configureLockerQr(newVal)
					} else {
						this.lockerQr.qrcode.makeCode("123")
					}
					this.syncLockerQrSrc()
				} else {
					this.lockerQr.src = null
				}
			}
		}
	}
}
</script>